import { useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';
import {
    fetchSessions,
    sessionsReplaced,
    sessionAdded,
    sessionUpdated,
    sessionRemoved,
    selectSessions,
    selectSessionsStatus,
    selectIsSessionsInitializing,
} from './sessionsSlice';
import { requestLoad, consumeLoad, selectLoadRequest } from './sessionLoaderSlice';
import { requestSend, consumeSend, selectChatQueue } from './chatQueueSlice';
import {
    fetchDevAgents,
    fetchDevSkills,
    fetchDevModels,
    selectDevAgents,
    selectDevAgentsStatus,
    selectDevSkills,
    selectDevSkillsStatus,
    selectDevModels,
    selectDevModelsStatus,
} from './devToolsSlice';

// Domain-scoped hook aliases so future typed variants drop in cleanly.
export const useChatDispatch = useDispatch;
export const useChatSelector = useSelector;

export function useSessions() {
    return useChatSelector(selectSessions);
}

export function useSessionsStatus() {
    return useChatSelector(selectSessionsStatus);
}

export function useIsSessionsInitializing() {
    return useChatSelector(selectIsSessionsInitializing);
}

export function useSessionsActions() {
    const dispatch = useChatDispatch();
    return useMemo(
        () => ({
            fetch: () => dispatch(fetchSessions()),
            replaceSessions: (sessions) => dispatch(sessionsReplaced(sessions)),
            addSession: (session) => dispatch(sessionAdded(session)),
            updateSession: (id, patch) => dispatch(sessionUpdated({ id, patch })),
            removeSession: (id) => dispatch(sessionRemoved(id)),
        }),
        [dispatch],
    );
}

export function useSessionLoader() {
    const dispatch = useChatDispatch();
    return useMemo(
        () => ({
            requestLoad: (sessionId, source = 'manual') =>
                dispatch(requestLoad({ sessionId, source })),
            consumeLoad: () => dispatch(consumeLoad()),
        }),
        [dispatch],
    );
}

export function useSessionLoadRequest() {
    return useChatSelector(selectLoadRequest);
}

export function useChatQueue() {
    return useChatSelector(selectChatQueue);
}

export function useChatQueueActions() {
    const dispatch = useChatDispatch();
    return useMemo(
        () => ({
            send: (message, source = 'manual') => dispatch(requestSend({ message, source })),
            consume: () => dispatch(consumeSend()),
        }),
        [dispatch],
    );
}

export function useDevAgents() {
    return useChatSelector(selectDevAgents);
}

export function useDevAgentsStatus() {
    return useChatSelector(selectDevAgentsStatus);
}

export function useDevSkills() {
    return useChatSelector(selectDevSkills);
}

export function useDevSkillsStatus() {
    return useChatSelector(selectDevSkillsStatus);
}

export function useDevModels() {
    return useChatSelector(selectDevModels);
}

export function useDevModelsStatus() {
    return useChatSelector(selectDevModelsStatus);
}

export function useDevToolsActions() {
    const dispatch = useChatDispatch();
    return useMemo(
        () => ({
            fetchAgents: () => dispatch(fetchDevAgents()),
            fetchSkills: () => dispatch(fetchDevSkills()),
            fetchModels: () => dispatch(fetchDevModels()),
        }),
        [dispatch],
    );
}
